/**
 * Full Moon Friday — the weekly showtime.
 *
 * One night a week (Friday, UTC) the show changes: XP earned from finished
 * matches is doubled and One Night deals from a twisted pool. Everything here
 * is pure data + date math so server and client derive the SAME answer from
 * the same clock with no protocol traffic: the server applies the multiplier
 * and deals the twisted pool; the client renders the banner.
 *
 * The twist rotates weekly and deterministically (days-since-epoch week index),
 * so every table worldwide sees the same twist on the same Friday.
 *
 * Twists are POOL-CONFIG variants only — they add/remove cards from the One
 * Night pool, never change role behaviour. That keeps them engine-adjudicated
 * and simulatable: every twist must be sim-verified with
 * `TWIST=<id> npx tsx packages/engine/scripts/onenight-balance.ts` before it
 * enters the rotation (see ONE_NIGHT_SPEC.md §8 for the statistical bar).
 */
/** XP multiplier applied to match XP (kind 'game') on a full-moon night. */
export const FULL_MOON_XP_MULTIPLIER = 2;
export const FULL_MOON_TWISTS = {
    DOUBLE_JOKER: {
        id: 'DOUBLE_JOKER',
        name: 'Two Jokers',
        line: 'Two Jokers walk the boards tonight — trust no seat, not even your own.',
        delta: { JOKER: 1 },
    },
    SECOND_THIEF: {
        id: 'SECOND_THIEF',
        name: 'Second Thief',
        line: 'A second Thief works the crowd tonight — count your cards twice.',
        delta: { THIEF: 1 },
    },
    SEER_SLEEPS: {
        id: 'SEER_SLEEPS',
        name: 'The Seer Sleeps',
        line: 'The Seer sleeps tonight — the village walks blind under the full moon.',
        delta: { SEER: -1 },
    },
};
/** Rotation order. Index = week number mod length; append-only to keep dates stable. */
export const FULL_MOON_ROTATION = ['DOUBLE_JOKER', 'SECOND_THIEF', 'SEER_SLEEPS'];
const DAY_MS = 24 * 60 * 60 * 1000;
/** Friday, measured in UTC — one unambiguous global showtime, no player timezone exists. */
export function isFullMoonNight(at) {
    return at.getUTCDay() === 5;
}
/** Stable week index (weeks since epoch, UTC). Same everywhere, no locale math. */
export function fullMoonWeekIndex(at) {
    return Math.floor(at.getTime() / (7 * DAY_MS));
}
/** The twist in effect at `at`, or null when it isn't a full-moon night. */
export function fullMoonTwistFor(at) {
    if (!isFullMoonNight(at))
        return null;
    return FULL_MOON_ROTATION[fullMoonWeekIndex(at) % FULL_MOON_ROTATION.length] ?? null;
}
/**
 * Apply a twist to a One Night card distribution, rebalancing villagers so the
 * pool size is unchanged. Pure; returns a new record. A delta that would push
 * any count (or the villager remainder) below zero returns the base pool
 * unchanged — a mis-sized table silently plays the classic pool rather than an
 * illegal one.
 */
export function applyFullMoonTwist(base, twistId) {
    const twist = FULL_MOON_TWISTS[twistId];
    const out = { ...base };
    let shifted = 0;
    for (const [role, d] of Object.entries(twist.delta)) {
        const next = (out[role] ?? 0) + d;
        if (next < 0)
            return base;
        out[role] = next;
        shifted += d;
    }
    const villagers = (out.VILLAGER ?? 0) - shifted;
    if (villagers < 0)
        return base;
    out.VILLAGER = villagers;
    return out;
}
//# sourceMappingURL=fullMoon.js.map